import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DraftPost } from "../types";
import type { SingleCardBusy } from "../hooks/usePostActions";
import { ConnectedProfile } from "../../../../types/customer";
import { asConnectedProfileArray } from "../utils/connectedProfiles";
import { GoogleGMark, isGoogleBusinessProfilePlatform } from "./GoogleGMark";
import { PlatformPostCard } from "./PlatformPostCard";

interface SocialPostsPanelProps {
  posts: DraftPost[];
  onSaveSingleDraft: (post: DraftPost) => void | Promise<void>;
  onPublishSingleDraft: (post: DraftPost) => void | Promise<void>;
  onScheduleSingleDraft: (post: DraftPost) => void | Promise<void>;
  singleCardBusy: SingleCardBusy | null;
  onInsertImageFromLibrary?: (
    tempId: string,
    mediaType?: "image" | "video"
  ) => void | Promise<void>;
  /** Meta carousel: open My Library in multi-select mode.
   *  Pass platform so FB picker scopes to images only. */
  onInsertCarouselFromLibrary?: (tempId: string, platform?: string) => void | Promise<void>;
  /** LinkedIn document posts (SGL-174): open My Library filtered to PDFs. */
  onInsertDocumentFromLibrary?: (tempId: string) => void | Promise<void>;
  onInsertImageFromLocalFile?: (tempId: string) => void;
  onPostsChange?: (posts: DraftPost[]) => void;
  /** When set, the panel auto-scrolls to this post and briefly highlights it. */
  focusTempId?: string;
  hasConnectedAccounts?: boolean;
  connectedProfiles?: ConnectedProfile[];
}

type PlatformKey =
  | "youtube"
  | "facebook"
  | "instagram"
  | "linkedin"
  | "google_business"
  | "other";

function PlatformChipGlyph({ platformKey }: { platformKey: PlatformKey }) {
  switch (platformKey) {
    case "facebook":
      return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[#2d3748] text-[9px] font-bold text-white">
          f
        </span>
      );
    case "instagram":
      return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[#2d3748] text-white">
          <span className="material-icons text-[10px] leading-none">photo_camera</span>
        </span>
      );
    case "linkedin":
      return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[#2d3748] text-[8px] font-bold text-white">
          in
        </span>
      );
    case "youtube":
      return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-[#2d3748] text-white">
          <span className="material-icons text-[12px] leading-none">smart_display</span>
        </span>
      );
    case "google_business":
      return (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-white ring-1 ring-[#2d3748]/20">
          <GoogleGMark className="h-3.5 w-3.5 grayscale" />
        </span>
      );
    default:
      return (
        <span className="material-icons shrink-0 text-[16px] text-[#2d3748]">
          campaign
        </span>
      );
  }
}

function postPlatformMatchesConnectedProfile(
  postPlatform: string,
  cp: ConnectedProfile
): boolean {
  const platform = postPlatform.toLowerCase();
  const cpPlatform = cp.platform?.toLowerCase() || "";
  return (
    (platform.includes("facebook") && cpPlatform.includes("facebook")) ||
    (platform.includes("instagram") && cpPlatform.includes("instagram")) ||
    (platform.includes("linkedin") && cpPlatform.includes("linkedin")) ||
    (platform.includes("youtube") && cpPlatform.includes("youtube")) ||
    (isGoogleBusinessProfilePlatform(postPlatform) && isGoogleBusinessProfilePlatform(cp.platform))
  );
}

export const SocialPostsPanel: React.FC<SocialPostsPanelProps> = ({
  posts,
  onSaveSingleDraft,
  onPublishSingleDraft,
  onScheduleSingleDraft,
  singleCardBusy,
  onInsertImageFromLibrary,
  onInsertCarouselFromLibrary,
  onInsertDocumentFromLibrary,
  onInsertImageFromLocalFile,
  onPostsChange,
  focusTempId,
  hasConnectedAccounts,
  connectedProfiles,
}) => {
  const navigate = useNavigate();
  const safeProfiles = asConnectedProfileArray(connectedProfiles);
  const effectiveHasConnectedAccounts =
    typeof hasConnectedAccounts === "boolean"
      ? hasConnectedAccounts
      : safeProfiles.length > 0;

  const postRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [localPosts, setLocalPosts] = useState<DraftPost[]>(posts);
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<"save" | "schedule" | "publish" | null>(null);

  const getPlatformKey = (platform: string): PlatformKey => {
    const normalized = platform.toLowerCase();
    if (normalized.includes("youtube")) return "youtube";
    if (normalized.includes("facebook")) return "facebook";
    if (normalized.includes("instagram")) return "instagram";
    if (normalized.includes("linkedin")) return "linkedin";
    if (isGoogleBusinessProfilePlatform(platform)) return "google_business";
    return "other";
  };

  const platformLabels: Record<PlatformKey, string> = {
    youtube: "YouTube",
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    google_business: "Google Business",
    other: "Other",
  };

  const groupedPosts = useMemo(() => {
    const orderedKeys: PlatformKey[] = [
      "youtube",
      "facebook",
      "instagram",
      "linkedin",
      "google_business",
      "other",
    ];
    const groups: Record<PlatformKey, DraftPost[]> = {
      youtube: [],
      facebook: [],
      instagram: [],
      linkedin: [],
      google_business: [],
      other: [],
    };

    localPosts.forEach((post) => {
      groups[getPlatformKey(post.platform)].push(post);
    });

    return orderedKeys
      .map((key) => ({ key, posts: groups[key] }))
      .filter((group) => group.posts.length > 0);
  }, [localPosts]);

  const flatOrderedPosts = useMemo(
    () => groupedPosts.flatMap((g) => g.posts),
    [groupedPosts]
  );

  useEffect(() => {
    setLocalPosts((prev) => {
      const localOverrides = new Map(
        prev
          .filter((p) => p.linkedinTarget)
          .map((p) => [p.temp_id, p.linkedinTarget])
      );
      if (localOverrides.size === 0) return posts;
      return posts.map((p) =>
        localOverrides.has(p.temp_id) && !p.linkedinTarget
          ? { ...p, linkedinTarget: localOverrides.get(p.temp_id) }
          : p
      );
    });
    setSelectedPostIds(new Set());
  }, [posts]);

  const suppressFocusRef = useRef(true);

  useEffect(() => {
    // Aggressively force top scroll on mount
    const reset = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    };
    reset();
    const t1 = setTimeout(reset, 50);
    const t2 = setTimeout(reset, 150);
    const t3 = setTimeout(() => {
      suppressFocusRef.current = false;
    }, 600);
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  useEffect(() => {
    if (!focusTempId || suppressFocusRef.current) return;
    const el = postRefs.current.get(focusTempId);
    if (el) {
      const timer = window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 150);
      return () => window.clearTimeout(timer);
    }
  }, [focusTempId]);

  const handleUpdatePost = (updatedPost: DraftPost) => {
    setLocalPosts((prev) => {
      const updated = prev.map((p) =>
        p.temp_id === updatedPost.temp_id ? updatedPost : p
      );
      onPostsChange?.(updated);
      return updated;
    });
  };

  const handleEditingChange = React.useCallback(
    (tempId: string, isEditing: boolean) => {
      setEditingIds((prev) => {
        const next = new Set(prev);
        if (isEditing) next.add(tempId);
        else next.delete(tempId);
        return next;
      });
      if (!isEditing) {
        setErrorIds((prev) => {
          const next = new Set(prev);
          next.delete(tempId);
          return next;
        });
      }
    },
    []
  );

  const handleToggleSelectAll = () => {
    if (selectedPostIds.size === flatOrderedPosts.length) {
      setSelectedPostIds(new Set());
    } else {
      setSelectedPostIds(new Set(flatOrderedPosts.map((p) => p.temp_id)));
    }
  };

  const handleToggleSelect = (tempId: string) => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const isPostPlatformConnected = (post: DraftPost) =>
    safeProfiles.some((cp: ConnectedProfile) =>
      postPlatformMatchesConnectedProfile(post.platform, cp)
    );

  const runSinglePostAction = (
    post: DraftPost,
    fn: (p: DraftPost) => void | Promise<void>
  ) => {
    if (editingIds.has(post.temp_id)) {
      setErrorIds(new Set([post.temp_id]));
      const el = postRefs.current.get(post.temp_id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrorIds((prev) => {
      const next = new Set(prev);
      next.delete(post.temp_id);
      return next;
    });
    void fn(post);
  };

  const runBulkAction = async (action: "save" | "schedule" | "publish") => {
    if (selectedPostIds.size === 0 || bulkBusy) return;
    setBulkBusy(action);

    const postsToProcess = flatOrderedPosts.filter((p) =>
      selectedPostIds.has(p.temp_id)
    );

    const fn =
      action === "save"
        ? onSaveSingleDraft
        : action === "schedule"
        ? onScheduleSingleDraft
        : onPublishSingleDraft;

    // Run sequentially to leverage per-item success logic without overwhelming state
    for (const post of postsToProcess) {
      if (editingIds.has(post.temp_id)) {
        setErrorIds((prev) => new Set(prev).add(post.temp_id));
        continue;
      }
      await fn(post);
    }

    setSelectedPostIds(new Set());
    setBulkBusy(null);
  };

  const noConnectedAccounts = !effectiveHasConnectedAccounts;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <style>{`
        @keyframes slideDownStagger {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      {/* Platform chips */}
      <div className="flex shrink-0 items-center gap-3 overflow-x-auto border-b border-theme-border px-6 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {flatOrderedPosts.length > 0 && (
          <div className="flex shrink-0 items-center gap-2 pr-3 border-r border-theme-border/50">
            <input
              type="checkbox"
              checked={selectedPostIds.size === flatOrderedPosts.length && flatOrderedPosts.length > 0}
              onChange={handleToggleSelectAll}
              className="h-4 w-4 cursor-pointer rounded border-theme-border text-theme-primary focus:ring-theme-primary/50"
              id="selectAllToggle"
            />
            <label htmlFor="selectAllToggle" className="cursor-pointer text-[13px] font-semibold text-theme-fg">
              Select All
            </label>
          </div>
        )}
        {groupedPosts.map((group, idx) => (
          <div
            key={group.key}
            style={{ 
              animation: `slideDownStagger 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
              animationDelay: `${idx * 0.05}s`,
              opacity: 0 
            }}
            className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
              idx === 0
                ? "border-theme-primary/25 bg-theme-primary/10 text-theme-primary"
                : "border-theme-border bg-theme-background text-theme-fg-muted"
            }`}
          >
            <PlatformChipGlyph platformKey={group.key} />
            <span className="text-theme-fg">
              {platformLabels[group.key]}
            </span>
            <span className="opacity-70">× {group.posts.length}</span>
          </div>
        ))}
      </div>

      {/* Scrollable previews */}
      <div
        ref={scrollContainerRef}
        className="custom-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto bg-theme-background/50 px-6 py-6"
      >
        {noConnectedAccounts && localPosts.length > 0 && (
          <div className="chat-brand-missing-banner mb-2 rounded-xl border border-[#c9a86c]/45 bg-[#fadfba]/80 p-4 shadow-sm ring-1 ring-amber-900/10 backdrop-blur-[2px]">
            <p className="mb-3 text-sm text-theme-fg">
              You do not have any social accounts connected. Until connected, you will need to manually post these posts on each platform.
            </p>
            <button
              type="button"
              onClick={() => navigate("/social-posts?tab=connect")}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              Connect Accounts
            </button>
          </div>
        )}
        {flatOrderedPosts.map((post, idx) => {
          const connected = isPostPlatformConnected(post);
          const hasScheduleTime = Boolean(post.scheduled_time?.trim());
          const busy =
            singleCardBusy?.tempId === post.temp_id ? singleCardBusy.action : null;

          return (
            <div
              key={post.temp_id}
              ref={(el) => {
                if (el) postRefs.current.set(post.temp_id, el);
                else postRefs.current.delete(post.temp_id);
              }}
              style={{ 
                animation: `slideDownStagger 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                animationDelay: `${(idx + 2) * 0.1}s`,
                opacity: 0 
              }}
              className="transition-all duration-500"
            >
              <PlatformPostCard
                post={post}
                uploadMedia={
                  onInsertImageFromLibrary && onInsertImageFromLocalFile
                    ? {
                        onLibrary: () =>
                          void onInsertImageFromLibrary(
                            post.temp_id,
                            post.platform.toUpperCase().includes("INSTAGRAM") &&
                              post.instagram_content_format === "video"
                              ? "video"
                              : "image"
                          ),
                        onLocalFile: () => onInsertImageFromLocalFile(post.temp_id),
                        onLibraryCarousel: onInsertCarouselFromLibrary
                          ? () => void onInsertCarouselFromLibrary(post.temp_id, post.platform)
                          : undefined,
                        onLibraryDocument: onInsertDocumentFromLibrary
                          ? () => void onInsertDocumentFromLibrary(post.temp_id)
                          : undefined,
                      }
                    : undefined
                }
                onUpdate={handleUpdatePost}
                onEditingChange={(isEditing) =>
                  handleEditingChange(post.temp_id, isEditing)
                }
                showUnsavedError={errorIds.has(post.temp_id)}
                isSelected={selectedPostIds.has(post.temp_id)}
                onToggleSelect={() => handleToggleSelect(post.temp_id)}
                connectedProfiles={safeProfiles}
                cardActions={{
                  onSave: () =>
                    runSinglePostAction(post, onSaveSingleDraft),
                  onSchedule: () =>
                    runSinglePostAction(post, onScheduleSingleDraft),
                  onPublish: () =>
                    runSinglePostAction(post, onPublishSingleDraft),
                  scheduleDisabled: !hasScheduleTime || !connected,
                  publishDisabled: !connected,
                  isPlatformConnected: connected,
                  busyAction: busy,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Hint strip / Bulk Actions */}
      <div className="shrink-0 border-t border-theme-border bg-theme-surface px-6 py-4">
        {selectedPostIds.size > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[13px] font-bold text-theme-fg">
              {selectedPostIds.size} post{selectedPostIds.size > 1 ? "s" : ""} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={bulkBusy !== null}
                onClick={() => runBulkAction("save")}
                className="rounded-lg border border-theme-border bg-theme-surface px-4 py-2 text-[13px] font-semibold text-theme-fg shadow-sm transition-all hover:bg-theme-background disabled:opacity-50"
              >
                {bulkBusy === "save" ? "Saving..." : "Save Selected"}
              </button>
              <button
                type="button"
                disabled={
                  bulkBusy !== null ||
                  !flatOrderedPosts
                    .filter((p) => selectedPostIds.has(p.temp_id))
                    .every((p) => isPostPlatformConnected(p))
                }
                title={
                  !flatOrderedPosts
                    .filter((p) => selectedPostIds.has(p.temp_id))
                    .every((p) => isPostPlatformConnected(p))
                    ? "Some selected posts are not connected to a platform"
                    : "Publish selected posts"
                }
                onClick={() => runBulkAction("publish")}
                className="rounded-lg bg-theme-primary px-4 py-2 text-[13px] font-bold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkBusy === "publish" ? "Publishing..." : "Publish Selected"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-center text-[12px] leading-relaxed text-theme-fg-muted">
              <span className="font-medium text-theme-fg">Save</span> keeps this post in your library without publishing.
              {" "}
              <span className="font-medium text-theme-fg">Schedule</span> needs a date/time and a connected account.
              {" "}
              <span className="font-medium text-theme-fg">Publish</span> sends now and needs a connected account.
            </p>
            {noConnectedAccounts && localPosts.length > 0 && (
              <p className="mt-2 text-center text-[12px] text-theme-fg-muted">
                Connect platforms under{" "}
                <span className="font-semibold text-theme-fg">Connections</span> to use Schedule or Publish.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
